import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { translations, defaultLanguage, getNestedValue } from '../i18n';

const LanguageContext = createContext(null);

const STORAGE_KEY = 'xoclub_language';

const detectBrowserLanguage = () => {
  // FRANCE: Forcer le français par défaut pour le marché principal
  const browserLang = navigator.language || navigator.userLanguage || 'fr';
  const langCode = browserLang.split('-')[0];
  
  // Si la langue n'est pas supportée, utiliser le français
  if (!translations[langCode]) {
    return 'fr';
  }
  
  return langCode;
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguageState] = useState(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && translations[stored]) {
      return stored;
    }
    return detectBrowserLanguage();
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, language);
    document.documentElement.lang = language;
  }, [language]);

  const setLanguage = useCallback((lang) => {
    if (translations[lang]) {
      setLanguageState(lang);
    }
  }, []);

  const t = useCallback((key, params = {}) => {
    const translation = getNestedValue(translations[language], key);
    
    if (!translation) {
      console.warn(`Translation missing for key: ${key}`);
      return key;
    }

    if (typeof translation !== 'string') {
      return translation;
    }

    return translation.replace(/\{\{(\w+)\}\}/g, (_, param) => {
      return params[param] !== undefined ? params[param] : `{{${param}}}`;
    });
  }, [language]);

  const value = {
    language,
    setLanguage,
    t
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const useTranslation = () => {
  const { t, language } = useLanguage();
  return { t, language };
};
